import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { ZodB2bSavingPlanCategory, ZodB2bSavingPlanType } from '@Common/types/b2b/plans';
import { ZodWithdrawalAllocations } from '@Common/types/b2b/withdraw';
import { ZodBackofficeGroupsT } from '@Common/types/bo/authorization';
import { ZodSavingsFundsRequestCancelBlocker } from '@Common/types/bo/b2b/savings_funds_requests';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { ZodTaskStates } from '@Common/types/bo/tasks';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { zodIntegerAmount2 } from '@Common/types/currency/currency';
import { ZodDate } from '@Common/types/date';
import { ZodPlannedWithdrawalFlow } from '@Common/types/funding/planned_withdrawal';
import { ZodB2BEarlyWithdrawalReason } from '@Common/types/funding/withdraw_early_request';
import { ZodMdlrPaymentStatus } from '@Common/types/modulr';
import { ZodProfileSavingPlanId } from '@Common/types/profile/saving_plans';
import { MdlrAccountTypeValues } from '@Common/types/qa/modulr';
import { ZodId } from '@Common/types/request';
import {
  ZodBankAccountBic,
  ZodBankAccountIban,
  ZodIntlAccountNumber,
  ZodNonSepaIntlBic,
  ZodUkAccountNumber,
  ZodUkSortCode,
} from '@Common/types/zod/bank_account';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const B2bWithdrawalStatusValues = [
  'new',
  'pending',
  'completed',
  'pending_approval',
  'cancelled',
  'settled',
] as const;
export type B2bWithdrawalStatus = (typeof B2bWithdrawalStatusValues)[number];
export const zodB2bWithdrawalStatus = z.enum(B2bWithdrawalStatusValues);

export const B2bWithdrawalTypeValues = ['regular', 'early'] as const;
export type B2bWithdrawalType = (typeof B2bWithdrawalTypeValues)[number];
export const zodB2bWithdrawalType = z.enum(B2bWithdrawalTypeValues);

export const ZodB2bWithdrawalOrdersFlow = ZodPlannedWithdrawalFlow.or(z.literal('regular'));
export type B2bWithdrawalOrdersFlow = z.TypeOf<typeof ZodB2bWithdrawalOrdersFlow>;

export const B2bWithdrawalDestinationAccountWarningValues = [
  'high_risk_country',
  'country_under_surveillance',
] as const;
export type B2bWithdrawalDestinationAccountWarning =
  (typeof B2bWithdrawalDestinationAccountWarningValues)[number];
export const zodB2bWithdrawalDestinationAccountWarning = z.enum(
  B2bWithdrawalDestinationAccountWarningValues,
);

const zodGetB2bWithdrawalsOne = z.object({
  id: ZodId,
  user_id: ZodId,
  company_id: ZodId,
  b2b_campaign_id: ZodId.nullable(),
  contract_id: ZodId,
  investing_product: ZodB2bSavingPlanCategory,
  b2b_company_plan_ids: z.array(z.number().int()),
  mdlr_beneficiary_id: ZodId.nullable(),
  account_type: z.enum(MdlrAccountTypeValues).nullable(),
  sort_code: ZodTrimmedString.nullable(),
  account_number: ZodTrimmedString.nullable(),
  bank_country: ZodTrimmedString.nullable(),
  status: zodB2bWithdrawalStatus,
  type: zodB2bWithdrawalType,
  orders_flow: ZodB2bWithdrawalOrdersFlow,
  is_planned_withdrawal: z.boolean(),
  gross_amount: z.number().nullable(),
  net_amount: z.number().nullable(),
  tax_amount: z.number().nullable(),
  initial_amount: z.number(),
  early_withdraw_reason: ZodB2BEarlyWithdrawalReason.nullable(),
  early_withdraw_event_date: ZodDate.nullable(),
  currency: ZodCurrency,
  employee_id: ZodId,
  savings_funds_requests_id: ZodId.nullable(),
  created_at: z.date(),
  updated_at: z.date(),
  paid_at: z.date().nullable(),
  payment_status: ZodMdlrPaymentStatus.nullable(),
  assets_class: z.enum(['ccb', 'mutual-fund']),
  bo_task_id: ZodId,
  destination_account_warning: zodB2bWithdrawalDestinationAccountWarning.nullable(),
  correlation_id: ZodTrimmedString,
  firstname: z.string().nullable(),
  lastname: z.string().nullable(),
});

export type GetB2bWithdrawalsOne = z.TypeOf<typeof zodGetB2bWithdrawalsOne>;
export const zodGetB2bWithdrawalsResponse = z
  .object({
    ...zodGetB2bWithdrawalsOne.omit({
      b2b_company_plan_ids: true,
      b2b_campaign_id: true,
      assets_class: true,
      destination_account_warning: true,
      savings_funds_requests_id: true,
    }).shape,
    bo_task_id: ZodId,
    task_state: ZodTaskStates.nullable(),
    task_assignee_group: ZodBackofficeGroupsT.nullable(),
    task_assignee: ZodTrimmedString.nullable(),
  })
  .array();
export type GetB2bWithdrawalsResponse = z.TypeOf<typeof zodGetB2bWithdrawalsResponse>;

export const getB2bWithdrawalsSortColumns: Exclude<
  keyof GetB2bWithdrawalsOne,
  | 'b2b_company_plan_ids'
  | 'b2b_campaign_id'
  | 'assets_class'
  | 'destination_account_warning'
  | 'savings_funds_requests_id'
>[] = [
  'id',
  'user_id',
  'company_id',
  'contract_id',
  'mdlr_beneficiary_id',
  'account_type',
  'sort_code',
  'account_number',
  'bank_country',
  'status',
  'orders_flow',
  'gross_amount',
  'net_amount',
  'tax_amount',
  'initial_amount',
  'currency',
  'created_at',
  'updated_at',
  'correlation_id',
];

export const ZodFilters = z
  .object({
    id: createZodArrayable(ZodId),
    user_id: createZodArrayable(ZodId),
    company_id: createZodArrayable(ZodId),
    contract_id: createZodArrayable(ZodId),
    type: createZodArrayable(zodB2bWithdrawalType),
    employee_id: createZodArrayable(ZodId),
    mdlr_beneficiary_id: createZodArrayable(ZodId),
    account_number: createZodArrayable(ZodTrimmedString),
    bank_country: createZodArrayable(ZodTrimmedString),
    status: createZodArrayable(zodB2bWithdrawalStatus),
    investing_product: createZodArrayable(ZodB2bSavingPlanCategory),
    task_state: createZodArrayable(ZodTaskStates),
    task_assignee: createZodArrayable(ZodTrimmedString),
    bo_task_id: createZodArrayable(ZodId),
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
    hide_campaign_withdrawals: z.boolean(),
    correlation_id: createZodArrayable(ZodTrimmedString),
  })
  .partial();

export const ZodGetB2bWithdrawalsQuery = buildZodBoListQueryParsed(
  ZodFilters,
  getB2bWithdrawalsSortColumns,
  { range: [0, 10000] },
);

export type GetB2bWithdrawalsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFilters>,
  (typeof getB2bWithdrawalsSortColumns)[number]
>;

const ZodB2bWithdrawalDocumentBase = z.object({
  type: z.enum(['early_withdrawal_request', 'proof_of_identity']),
  name: ZodTrimmedString,
  date_of_expiry: ZodDate.optional(),
});

const ZodB2bWithdrawalBase64Document = z.object({
  ...ZodB2bWithdrawalDocumentBase.shape,
  file: ZodTrimmedString,
});

const ZodB2bWithdrawalPresignedDocument = z.object({
  ...ZodB2bWithdrawalDocumentBase.shape,
  presigned_path: ZodTrimmedString,
});

const ZodB2bWithdrawalDocument = z.union([
  ZodB2bWithdrawalBase64Document,
  ZodB2bWithdrawalPresignedDocument,
]);
export type B2bWithdrawalDocument =
  PostB2bWithdrawalRequestEndpoint['Client']['RequestBody']['documents'][number];

const ZodThirdPartyBankAccountAddress = z.object({
  street: ZodTrimmedString.nullable().optional(),
  city: ZodTrimmedString.nullable().optional(),
  postcode: ZodTrimmedString.nullable().optional(),
  country: ZodTrimmedString.nullable().optional(),
});

const ZodThirdPartyBankAccountBase = z.object({
  first_name: ZodTrimmedString,
  last_name: ZodTrimmedString.nullable().optional(),
  presigned_path: ZodTrimmedString.nullable(),
  document_name: ZodTrimmedString.nullable(),
  address: ZodThirdPartyBankAccountAddress.nullable().optional(),
});

const ZodThirdPartyBankAccountIban = z.object({
  ...ZodThirdPartyBankAccountBase.shape,
  account_type: z.literal('iban'),
  iban: ZodBankAccountIban,
  bic: ZodBankAccountBic,
});

const ZodThirdPartyBankAccountIntl = z.object({
  ...ZodThirdPartyBankAccountBase.shape,
  account_type: z.literal('intl'),
  account_number: ZodIntlAccountNumber,
  bic: ZodNonSepaIntlBic,
});

const ZodThirdPartyBankAccountScan = z.object({
  ...ZodThirdPartyBankAccountBase.shape,
  account_type: z.literal('scan'),
  sort_code: ZodUkSortCode,
  account_number: ZodUkAccountNumber,
  bic: ZodBankAccountBic,
});

export const ZodThirdPartyBankAccount = z.discriminatedUnion('account_type', [
  ZodThirdPartyBankAccountIban,
  ZodThirdPartyBankAccountIntl,
  ZodThirdPartyBankAccountScan,
]);

export type ThirdPartyBankAccount = z.TypeOf<typeof ZodThirdPartyBankAccount>;

const ZodPostB2bWithdrawalRequestBody = z
  .object({
    employee_id: ZodId,
    company_id: ZodId,
    beneficiary_id: ZodId.optional(),
    third_party_bank_account: ZodThirdPartyBankAccount.optional(),
    early_event_date: ZodDate.nullable(),
    early_reason: ZodB2BEarlyWithdrawalReason.nullable(),
    allocations: ZodWithdrawalAllocations,
    documents: z.array(ZodB2bWithdrawalDocument),
  })
  .refine(
    (data) => {
      const hasBeneficiary = data.beneficiary_id !== undefined;
      const hasThirdParty = data.third_party_bank_account !== undefined;
      return hasBeneficiary !== hasThirdParty;
    },
    {
      message: 'Either beneficiary_id or third_party_bank_account must be provided',
    },
  )
  .refine((data) => data.early_event_date === null || data.early_reason !== null, {
    message: 'early_reason is required for early withdrawals',
    path: ['early_reason'],
  });

const ZodIdResponse = z.object({
  id: ZodId,
});

export const getB2bWithdrawalsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bWithdrawalsQuery,
  responseBody: zodGetB2bWithdrawalsResponse,
});

export type GetB2bWithdrawalsEndpoint = GetEndpointType<typeof getB2bWithdrawalsEndpoint>;

export const ZodB2bWithdrawalParams = createZodParamsValidator(
  z.object({
    id: ZodId,
  }),
);

/**
 * Reasons a withdrawal cannot be cancelled from the back office, on top of the ones of its fund
 * request. The back office maps them to a message shown when hovering the disabled "Cancel" button.
 */
export const B2bWithdrawalCancelBlockerValues = [
  'unsupported_assets_class',
  'not_approved',
  'already_cancelled',
  'payout_payment_exists',
] as const;

export const ZodB2bWithdrawalCancelBlocker = z.union([
  z.enum(B2bWithdrawalCancelBlockerValues),
  ZodSavingsFundsRequestCancelBlocker,
]);

export type B2bWithdrawalCancelBlocker = z.TypeOf<typeof ZodB2bWithdrawalCancelBlocker>;

const zodGetB2bWithdrawalDetails = z.object({
  ...zodGetB2bWithdrawalsOne.shape,
  /**
   * `null` when the withdrawal can be cancelled.
   */
  cancel_blocker: ZodB2bWithdrawalCancelBlocker.nullable(),
});

export const getB2bWithdrawalEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodB2bWithdrawalParams,
  responseBody: zodGetB2bWithdrawalDetails,
});

export type GetB2bWithdrawalEndpoint = GetEndpointType<typeof getB2bWithdrawalEndpoint>;

const ZodPutB2bWithdrawalCancelBody = z.object({
  reason: ZodTrimmedString.min(1),
  description: ZodTrimmedString.optional(),
});

export const putB2bWithdrawalCancelEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodB2bWithdrawalParams,
  body: ZodPutB2bWithdrawalCancelBody,
  responseBody: ZodIdResponse,
});

export type PutB2bWithdrawalCancelEndpoint = GetEndpointType<typeof putB2bWithdrawalCancelEndpoint>;

export const postB2bWithdrawalRequestEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bWithdrawalRequestBody,
  responseBody: ZodIdResponse,
});

export type PostB2bWithdrawalRequestEndpoint = GetEndpointType<
  typeof postB2bWithdrawalRequestEndpoint
>;

const ZodPostB2bWithdrawalAmountsBody = z.object({
  employee_id: ZodId,
  company_id: ZodId,
  early_event_date: ZodDate.nullable().optional(),
  early_reason: ZodB2BEarlyWithdrawalReason.nullable().optional(),
  allocations: ZodWithdrawalAllocations,
});

const ZodPostB2bWithdrawalAmountsResponseBody = z.object({
  id: ZodId,
  gross_amount: z.number().int(),
  net_amount: z.number().int(),
  tax_amount: z.number().int(),
  currency: ZodCurrency,
});

export const postB2bWithdrawalAmountsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bWithdrawalAmountsBody,
  responseBody: ZodPostB2bWithdrawalAmountsResponseBody,
});

export type PostB2bWithdrawalAmountsEndpoint = GetEndpointType<
  typeof postB2bWithdrawalAmountsEndpoint
>;

const ZodPostB2bWithdrawalEligibleAllocationsBody = z.object({
  employee_id: ZodId,
  company_id: ZodId,
  products: z
    .array(ZodB2bSavingPlanCategory)
    .min(1)
    .refine(
      (products) => {
        const uniqueProducts = new Set(products);
        return products.length === uniqueProducts.size;
      },
      {
        message: 'Products must be unique',
        path: ['products'],
      },
    ),
  early_event_date: ZodDate,
  early_reason: ZodB2BEarlyWithdrawalReason,
});

const ZodPositionData = z.object({
  instrument_id: ZodId,
  amount: zodIntegerAmount2,
  short_name: ZodTrimmedString.nullable(),
  symbol: ZodTrimmedString,
});

const ZodPlanData = z.object({
  plan_id: ZodProfileSavingPlanId,
  type: ZodB2bSavingPlanType,
  category: ZodB2bSavingPlanCategory,
  withdrawable: zodIntegerAmount2,
  locked: zodIntegerAmount2,
  total: zodIntegerAmount2,
  withdrawable_positions: ZodPositionData.array(),
  locked_positions: ZodPositionData.array(),
});

const ZodPostB2bWithdrawalEligibleAllocationsResponseBody = z.object({
  id: ZodId,
  withdrawable: zodIntegerAmount2,
  locked: zodIntegerAmount2,
  total: zodIntegerAmount2,
  plans: ZodPlanData.array(),
  categories: z.array(
    z.object({
      type: ZodB2bSavingPlanCategory,
      withdrawable: zodIntegerAmount2,
      locked: zodIntegerAmount2,
      total: zodIntegerAmount2,
    }),
  ),
});

export type PostB2bWithdrawalEligibleAllocationsResponseBody = z.TypeOf<
  typeof ZodPostB2bWithdrawalEligibleAllocationsResponseBody
>;

export const postB2bWithdrawalEligibleAllocationsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bWithdrawalEligibleAllocationsBody,
  responseBody: ZodPostB2bWithdrawalEligibleAllocationsResponseBody,
});

export type PostB2bWithdrawalEligibleAllocationsEndpoint = GetEndpointType<
  typeof postB2bWithdrawalEligibleAllocationsEndpoint
>;

const ZodPostB2bWithdrawalBulkTemplateBody = z.object({
  b2b_company_id: ZodId,
  instrument_id: ZodId,
});

const ZodPostB2bWithdrawalBulkTemplateResponseBody = z.object({
  id: ZodId,
  url: ZodTrimmedString,
  file_name: ZodTrimmedString,
  file_path: ZodTrimmedString,
});

export const postB2bWithdrawalBulkTemplateEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bWithdrawalBulkTemplateBody,
  responseBody: ZodPostB2bWithdrawalBulkTemplateResponseBody,
});

export type PostB2bWithdrawalBulkTemplateEndpoint = GetEndpointType<
  typeof postB2bWithdrawalBulkTemplateEndpoint
>;

const ZodPostB2bWithdrawalBulkRow = z.object({
  employee_id: ZodId,
  quantity: z.number().min(0),
});

const ZodPostB2bWithdrawalBulkBody = z.object({
  b2b_company_id: ZodId,
  instrument_id: ZodId,
  rows: z.array(ZodPostB2bWithdrawalBulkRow).min(1).max(1000),
});

const ZodPostB2bWithdrawalBulkResponseBody = z.object({
  id: ZodId,
  created_count: z.number().int(),
});

export const postB2bWithdrawalBulkEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bWithdrawalBulkBody,
  responseBody: ZodPostB2bWithdrawalBulkResponseBody,
});

export type PostB2bWithdrawalBulkEndpoint = GetEndpointType<typeof postB2bWithdrawalBulkEndpoint>;

const ZodPostB2bWithdrawalBulkPositionsBody = z.object({
  b2b_company_id: ZodId,
  instrument_id: ZodId,
  employee_ids: z.array(ZodId).min(1).max(100),
});

const ZodPostB2bWithdrawalBulkPositionsResponseBody = z.object({
  id: ZodId,
  positions: z.array(
    z.object({
      employee_id: ZodId,
      company_plan_type: ZodTrimmedString.nullable(),
      quantity: z.number(),
      amount: z.number().int(),
      position_date: ZodDate,
      is_deceased: z.boolean(),
    }),
  ),
});

export const postB2bWithdrawalBulkPositionsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bWithdrawalBulkPositionsBody,
  responseBody: ZodPostB2bWithdrawalBulkPositionsResponseBody,
});

export type PostB2bWithdrawalBulkPositionsEndpoint = GetEndpointType<
  typeof postB2bWithdrawalBulkPositionsEndpoint
>;

export const ZodB2bWithdrawalBulkPayoutInformationParams = createZodParamsValidator(
  z.object({
    id: ZodId,
  }),
);

const MdlrBeneficiaryActiveStatusValues = ['active', 'pending'] as const;
const ZodMdlrBeneficiaryActiveStatus = z.enum(MdlrBeneficiaryActiveStatusValues);

const ZodGetB2bWithdrawalBulkPayoutInformationResponseBody = z
  .object({
    id: ZodId,
  })
  .and(
    z.discriminatedUnion('target', [
      z.object({ target: z.literal('employee') }),
      z.object({
        target: z.literal('company'),
        beneficiary: z
          .object({
            status: ZodMdlrBeneficiaryActiveStatus.nullable(),
            iban: ZodTrimmedString.nullable(),
            name: ZodTrimmedString.nullable(),
          })
          .nullable(),
      }),
    ]),
  );

export const getB2bWithdrawalBulkPayoutInformationEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodB2bWithdrawalBulkPayoutInformationParams,
  responseBody: ZodGetB2bWithdrawalBulkPayoutInformationResponseBody,
});

export type GetB2bWithdrawalBulkPayoutInformationEndpoint = GetEndpointType<
  typeof getB2bWithdrawalBulkPayoutInformationEndpoint
>;
